
import React, { useState, useRef, useEffect } from 'react';
import { 
  ArrowRightLeft, 
  Loader2, 
  Copy, 
  Check, 
  Volume2, 
  Mic, 
  X, 
  Search, 
  History,
  Star,
  Clipboard,
  Zap
} from 'lucide-react';
import { AppLanguage } from '../types';
import { translateText, generateSpeech } from '../services/gemini';

const WORLD_LANGUAGES = [
  'Detect Language', 'Zomi', 'English', 'Burmese', 'Mizo', 'Falam Chin', 'Hakha Chin',
  'Afrikaans', 'Albanian', 'Amharic', 'Arabic', 'Armenian', 'Azerbaijani', 'Basque', 'Belarusian', 
  'Bengali', 'Bosnian', 'Bulgarian', 'Catalan', 'Cebuano', 'Chichewa', 'Chinese (Simplified)', 
  'Chinese (Traditional)', 'Corsican', 'Croatian', 'Czech', 'Danish', 'Dutch', 'Esperanto', 
  'Estonian', 'Filipino', 'Finnish', 'French', 'Frisian', 'Galician', 'Georgian', 'German', 
  'Greek', 'Gujarati', 'Haitian Creole', 'Hausa', 'Hawaiian', 'Hebrew', 'Hindi', 'Hmong', 
  'Hungarian', 'Icelandic', 'Igbo', 'Indonesian', 'Irish', 'Italian', 'Japanese', 'Javanese', 
  'Kannada', 'Kazakh', 'Khmer', 'Kinyarwanda', 'Korean', 'Kurdish (Kurmanji)', 'Kyrgyz', 'Lao', 
  'Latin', 'Latvian', 'Lithuanian', 'Luxembourgish', 'Macedonian', 'Malagasy', 'Malay', 'Malayalam', 
  'Maltese', 'Maori', 'Marathi', 'Mongolian', 'Myanmar (Burmese)', 'Nepali', 'Norwegian', 'Odia (Oriya)', 
  'Pashto', 'Persian', 'Polish', 'Portuguese', 'Punjabi', 'Romanian', 'Russian', 'Samoan', 
  'Scots Gaelic', 'Serbian', 'Sesotho', 'Shona', 'Sindhi', 'Sinhala', 'Slovak', 'Slovenian', 
  'Somali', 'Spanish', 'Sundanese', 'Swahili', 'Swedish', 'Tajik', 'Tamil', 'Tatar', 'Telugu', 
  'Thai', 'Turkish', 'Turkmen', 'Ukrainian', 'Urdu', 'Uyghur', 'Uzbek', 'Vietnamese', 'Welsh', 
  'Xhosa', 'Yiddish', 'Yoruba', 'Zulu'
];

const VOICES = [
  { name: 'Kore', label: 'Professional', gender: 'Male' },
  { name: 'Puck', label: 'Energetic', gender: 'Male' },
  { name: 'Zephyr', label: 'Warm', gender: 'Female' },
  { name: 'Fenrir', label: 'Authoritative', gender: 'Male' }
];

const TranslateView: React.FC<{ onUseCredit: () => boolean, isPro: boolean, language: AppLanguage }> = ({ onUseCredit, isPro, language }) => {
  const [inputText, setInputText] = useState('');
  const [outputText, setOutputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  
  const [fromLang, setFromLang] = useState('Detect Language');
  const [toLang, setToLang] = useState('Zomi');
  
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(VOICES[0]);
  const [selectingLang, setSelectingLang] = useState<'from' | 'to' | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pasteError, setPasteError] = useState(false);
  
  const audioContextRef = useRef<AudioContext | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleTranslate = async (textToTranslate = inputText) => {
    if (!textToTranslate.trim() || isLoading) return;
    if (!onUseCredit()) return;

    setIsLoading(true);
    try {
      const source = fromLang === 'Detect Language' ? 'auto-detect' : fromLang;
      const result = await translateText(textToTranslate, source, toLang, isPro);
      setOutputText(result || '');
      setSaved(false);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputText.trim().length > 1) {
        handleTranslate();
      } else if (!inputText.trim()) {
        setOutputText('');
      }
    }, 1500); 
    return () => clearTimeout(timer);
  }, [inputText, fromLang, toLang]);

  const handleListen = async (text: string) => {
    if (!text || isSpeaking) return;
    setIsSpeaking(true);
    try {
      const base64Audio = await generateSpeech(text, selectedVoice.name);
      if (base64Audio) {
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
        }
        const ctx = audioContextRef.current;
        const binaryString = atob(base64Audio);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
        const dataInt16 = new Int16Array(bytes.buffer);
        const buffer = ctx.createBuffer(1, dataInt16.length, 24000);
        const channelData = buffer.getChannelData(0);
        for (let i = 0; i < dataInt16.length; i++) channelData[i] = dataInt16[i] / 32768.0;
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.onended = () => setIsSpeaking(false);
        source.start(0);
      }
    } catch (error) {
      setIsSpeaking(false);
    }
  };

  const swapLanguages = () => {
    if (fromLang === 'Detect Language') return;
    const oldFrom = fromLang;
    const oldTo = toLang;
    setFromLang(oldTo);
    setToLang(oldFrom);
    if (outputText) {
      const oldIn = inputText;
      setInputText(outputText);
      setOutputText(oldIn);
    }
  };

  const handlePaste = async () => {
    setPasteError(false);
    try {
      if (navigator.clipboard && navigator.clipboard.readText) {
        const text = await navigator.clipboard.readText();
        setInputText(text);
      } else {
        throw new Error('Clipboard API unavailable');
      }
    } catch (err) {
      setPasteError(true);
      setTimeout(() => setPasteError(false), 3000);
      if (inputRef.current) inputRef.current.focus();
    }
  };

  const filteredLangs = WORLD_LANGUAGES.filter(l => 
    l.toLowerCase().includes(searchQuery.toLowerCase()) && (selectingLang === 'to' ? l !== 'Detect Language' : true)
  ).sort((a, b) => a === 'Detect Language' ? -1 : a.localeCompare(b));

  return (
    <div className="w-full h-full flex flex-col bg-white dark:bg-[#0d0d0d] text-slate-900 dark:text-white transition-colors duration-300 overflow-hidden font-sans select-none">
      
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-4 pb-4 z-30 border-b border-slate-100 dark:border-white/5 bg-white dark:bg-[#0d0d0d]">
        <button className="p-2 -ml-2 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors">
          <History size={22} />
        </button>
        <h1 className="text-[16px] font-bold tracking-tight text-slate-800 dark:text-white/90">Zomi Translate</h1>
        <div className="w-10"></div>
      </div>

      <div className="flex-1 flex flex-col overflow-y-auto no-scrollbar pt-2 pb-10">
        
        {/* Input Region */}
        <div className="w-full max-w-2xl mx-auto px-6 pt-6 pb-2 flex flex-col min-h-[140px]">
          <textarea
            ref={inputRef}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Enter text"
            className="w-full flex-1 bg-transparent text-slate-900 dark:text-white text-[17px] md:text-[18px] font-medium placeholder:text-slate-300 dark:placeholder:text-slate-800 focus:outline-none resize-none leading-snug mb-4"
          />
          
          <div className="flex items-center justify-between mt-auto">
            <div className="flex items-center gap-4">
              <button 
                onClick={handlePaste}
                className="flex items-center gap-2 text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors py-2 active:scale-90 relative"
              >
                <Clipboard size={15} />
                <span className="text-[13px] font-bold">Paste</span>
                {pasteError && (
                  <span className="absolute -top-8 left-0 bg-red-500 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">
                    Paste manually
                  </span>
                )}
              </button>
            </div>
            
            <button 
              onClick={() => handleListen(inputText)}
              className="w-11 h-11 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center shadow-sm active:scale-95 transition-all ring-1 ring-slate-100 dark:ring-white/5"
            >
              <Mic size={18} className="text-blue-600 dark:text-blue-400" />
            </button>
          </div>
        </div>

        {/* Language Switcher Bar */}
        <div className="w-full max-w-2xl mx-auto px-5 py-6">
          <div className="flex items-center justify-between gap-1.5 bg-slate-50 dark:bg-[#111112] p-1.5 rounded-[1.25rem] border border-slate-100 dark:border-white/5">
            <button 
              onClick={() => setSelectingLang('from')}
              className="flex-1 bg-white dark:bg-[#1a1a1b] py-3 px-2 rounded-[1rem] flex items-center justify-center text-[13px] font-bold text-slate-700 dark:text-white/80 border border-slate-100 dark:border-white/5 active:bg-slate-100 dark:active:bg-white/10 transition-all truncate shadow-sm"
            >
              {fromLang}
            </button>
            
            <button 
              onClick={swapLanguages}
              disabled={fromLang === 'Detect Language'}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-all active:scale-90 disabled:opacity-20"
            >
              <ArrowRightLeft size={14} />
            </button>
            
            <button 
              onClick={() => setSelectingLang('to')}
              className="flex-1 bg-white dark:bg-[#1a1a1b] py-3 px-2 rounded-[1rem] flex items-center justify-center text-[13px] font-bold text-slate-700 dark:text-white/80 border border-slate-100 dark:border-white/5 active:bg-slate-100 dark:active:bg-white/10 transition-all truncate shadow-sm"
            >
              {toLang}
            </button>
          </div>
        </div>

        {/* Manual Translate Button */}
        <div className="w-full max-w-2xl mx-auto px-5 py-2">
          <button 
            onClick={() => handleTranslate()}
            disabled={!inputText.trim() || isLoading}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white py-3.5 rounded-2xl flex items-center justify-center gap-2 font-bold text-[13px] transition-all active:scale-[0.98] shadow-md shadow-blue-600/10"
          >
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Zap size={14} fill="currentColor" />}
            Translate
          </button>
        </div>

        {/* Translation Output Card */}
        <div className="w-full max-w-2xl mx-auto px-5 py-4 min-h-[100px]">
          {inputText.trim() && (
            <div className="bg-slate-50 dark:bg-[#1a1a1b] rounded-[1.75rem] p-6 border border-slate-100 dark:border-white/5 shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-400">
              {isLoading ? (
                <div className="flex items-center gap-3 py-2">
                  <Loader2 className="animate-spin text-blue-600 dark:text-blue-400" size={16} />
                  <span className="text-[9px] font-bold text-blue-600/40 dark:text-blue-400/40 uppercase tracking-widest">Translating...</span>
                </div>
              ) : (
                <>
                  <div className="text-[17px] font-bold text-blue-600 dark:text-blue-400 leading-snug mb-5 whitespace-pre-wrap">
                    {outputText || '...'}
                  </div>
                  <div className="flex items-center justify-between border-t border-slate-200 dark:border-white/5 pt-4">
                    <div className="flex items-center gap-4">
                      <button onClick={() => handleListen(outputText)} className="p-2 text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors active:scale-90">
                        <Volume2 size={18} />
                      </button>
                      <button onClick={() => { navigator.clipboard.writeText(outputText); setCopied(true); setTimeout(() => setCopied(false), 2000); }} className="p-2 text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors active:scale-90">
                        {copied ? <Check size={18} className="text-green-600" /> : <Copy size={18} />}
                      </button>
                    </div>
                    <button 
                      onClick={() => setSaved(!saved)}
                      className={`p-2 transition-all active:scale-90 ${saved ? 'text-blue-600 dark:text-blue-400' : 'text-slate-300 dark:text-slate-700 hover:text-slate-600'}`}
                    >
                      <Star size={18} fill={saved ? "currentColor" : "none"} />
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Language Selection Modal */}
      {selectingLang && (
        <div className="fixed inset-0 z-[100] bg-white dark:bg-[#0d0d0d] flex flex-col animate-in fade-in slide-in-from-bottom-6 duration-400">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-white/5 bg-white dark:bg-[#0d0d0d] sticky top-0">
            <Search size={18} className="text-slate-400 dark:text-slate-700 shrink-0" />
            <input
              autoFocus
              type="text"
              placeholder="Search languages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent border-none text-slate-800 dark:text-white text-[15px] font-bold focus:outline-none placeholder:text-slate-300 dark:placeholder:text-slate-800"
            />
            <button onClick={() => {setSelectingLang(null); setSearchQuery('');}} className="p-2 text-slate-400 hover:text-slate-900 active:scale-90 transition-all">
              <X size={22} />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto no-scrollbar p-4">
            <div className="max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 pb-20">
              {filteredLangs.map((lang) => {
                const isSelected = (selectingLang === 'from' ? fromLang === lang : toLang === lang);
                return (
                  <button 
                    key={lang}
                    onClick={() => {
                      if (selectingLang === 'from') setFromLang(lang);
                      else setToLang(lang);
                      setSelectingLang(null);
                      setSearchQuery('');
                    }}
                    className={`p-3.5 rounded-[1rem] text-[13px] font-bold text-left transition-all border ${isSelected ? 'bg-blue-600 text-white border-blue-500 shadow-sm' : 'bg-slate-50 dark:bg-white/5 text-slate-600 dark:text-slate-400 border-slate-100 dark:border-transparent hover:bg-slate-100 dark:hover:bg-white/10 active:bg-slate-200 dark:active:bg-white/10'}`}
                  >
                    {lang}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TranslateView;
